/**
 * sceneAtticEase — a real product page (Blue Bohemian Crystal Flower Vase,
 * attic-ease.com) rendered as motion instead of HTML.
 *
 * Same content the Shopify Dawn theme ships: brand wordmark, eyebrow,
 * product name, $140 USD, 4 product photos as a swap-gallery, copy block,
 * specs, CTA. The upgrade is the interaction:
 *   - Thumbnail tap swaps the hero image through a velocity nudge so the
 *     postprocess motion blur trails the swap (the wedge from gallery.ts).
 *   - Hero drifts 4px on cursor parallax.
 *   - Price tag has a subtle breathing alpha.
 *   - Spec rows slide in left-to-right with a 60ms cascade on mount.
 *
 * Built for the user as a drop-in replacement demo — if they like it,
 * any Shopify Dawn PDP can be ported by changing the image URLs and copy.
 */

import {
  type Scene,
  createScene,
  makeRect,
  makeText,
  makeImage,
  addNode,
} from "../sceneGraph";

// Exact-product supplier photography for Aurum Crystal AU52046 / Plantica.
// These local assets replace the lower-detail Shopify reshoots. We keep
// separate 3:2 hero and 1:1 thumbnail canvases because NSPP currently
// stretches image textures to the node bounds; precomposing the masters at
// the actual render ratios preserves the vase's proportions.
const VASE_HERO_IMAGES = [
  "/products/aurum-au52046/hero-01.jpg",
  "/products/aurum-au52046/hero-02.jpg",
  "/products/aurum-au52046/hero-03.jpg",
  "/products/aurum-au52046/hero-04.jpg",
];

const VASE_THUMB_IMAGES = [
  "/products/aurum-au52046/thumb-01.jpg",
  "/products/aurum-au52046/thumb-02.jpg",
  "/products/aurum-au52046/thumb-03.jpg",
  "/products/aurum-au52046/thumb-04.jpg",
];

const VW = 1440;
const VH = 900;

export function sceneAtticEase(w: number, _h: number): Scene {
  const scene = createScene();
  let z = 0;
  const T = () => performance.now();
  const isPhone = w < 600;
  const pad = isPhone ? 20 : 64;

  // === Interaction state ============================================
  let activeIdx = 0;
  // Hero nudge state: on thumbnail tap, hero briefly translates ~14px in
  // a random direction so the velocity buffer + motion-blur postprocess
  // smears the swap. Same trick as gallery.ts.
  let heroNudgeUntil = 0;
  let heroNudgeX = 0;
  let heroNudgeY = 0;
  // Hero scale-up on swap — new image scales from 0.92 → 1.0 over 240ms
  // with overshoot. Visible in screenshots (the new hero will be slightly
  // smaller than the prior frame's) AND in motion (the blur trail catches
  // the scale velocity).
  let heroSwapUntil = 0;
  let heroSwapStartT = 0;
  // Cursor parallax — hero shifts ±6px based on pointer position. Driven
  // by reading engine.pointer (handled by an injected updater that the
  // engine ticks each frame — see onFrame below).
  let cursorNX = 0.5;
  let cursorNY = 0.5;
  // Tracked ring node — we delete and re-add on every active change so
  // only the selected thumb is outlined. Stored by id so we can find it.
  let activeRingId: number | null = null;

  // === Layout constants =============================================
  const HERO_X = pad;
  const HERO_Y = 110;
  const HERO_W = Math.min(720, Math.floor((VW - pad * 2) * 0.62));
  const HERO_H = Math.floor(VH * 0.62);

  const THUMB_W = 96;
  const THUMB_H = 96;
  const THUMB_GAP = 14;
  const THUMBS_X = HERO_X + HERO_W + 36;
  const THUMBS_Y0 = HERO_Y + 8;

  const COL_X = isPhone ? pad : THUMBS_X + THUMB_W + 80;
  const COL_W = w - COL_X - pad;

  // === Backdrop — soft warm off-white, like the real PDP ============
  addNode(scene, makeRect({
    x: 0, y: 0, w: VW, h: VH,
    fill: { r: 0.992, g: 0.988, b: 0.976, a: 1 },
  })); z++;

  // Hero stage — same off-white as the page background, sized to
  // wrap the hero image with a small margin. Combined with the hero
  // image's mix-blend-mode: multiply, the JPG's baked-in white
  // background (#FFFFFF) blends invisibly into this stage which
  // itself blends into the page. Net: no visible photo frame,
  // just the vase floating on the page.
  addNode(scene, makeRect({
    x: HERO_X - 16, y: HERO_Y - 16, w: HERO_W + 32, h: HERO_H + 32,
    fill: { r: 0.992, g: 0.988, b: 0.976, a: 1 },
    radius: 6,
    tagName: "attic-hero-stage",
  })); z++;

  // Subtle vignette — adds depth without competing with the product
  addNode(scene, makeRect({
    x: 0, y: 0, w: VW, h: VH,
    fill: { r: 0.94, g: 0.92, b: 0.86, a: 0.18 },
    gradientEnd: { r: 0.992, g: 0.988, b: 0.976, a: 0 },
    gradientAngle: Math.PI / 2,
  })); z++;

  // === Header / nav =================================================
  // Brand wordmark — left side (matches real Attic-Ease PDP)
  addNode(scene, makeText({
    x: pad, y: 36, w: 200, h: 18,
    text: "Attic-Ease",
    fontSize: 18, fontWeight: 700,
    textColor: { r: 0.06, g: 0.06, b: 0.06, a: 1 },
    letterSpacing: -0.4,
    fontFamily: "Georgia, 'Times New Roman', serif",
  })); z++;

  // Nav row — centered. Real Attic-Ease puts nav in the middle of the
  // header. We center the row by laying items out at x = w/2 - totalW/2.
  if (!isPhone) {
    const navItems = ["Attic Ease Kits", "Home Décor", "Gifts", "About", "Contact"];
    const itemW = 110;
    const gap = 8;
    const totalW = navItems.length * itemW + (navItems.length - 1) * gap;
    const navStart = Math.floor(w / 2 - totalW / 2);
    let cx = navStart;
    const navY = 40;
    for (let i = 0; i < navItems.length; i++) {
      const text = navItems[i]!;
      addNode(scene, makeText({
        x: cx, y: navY, w: itemW, h: 14,
        text,
        fontSize: 11, fontWeight: 500,
        textColor: { r: 0.16, g: 0.16, b: 0.16, a: 0.85 },
        letterSpacing: 0.2,
        textAlign: "center",
        fontFamily: "system-ui, -apple-system, sans-serif",
      })); z++;
      cx += itemW + gap;
    }
  }

  // Right-side icons (Search / Account / Cart) — just three small dots
  // since we can't render real glyph icons in NSPP primitives. Real
  // visual cue that there's a header on this side.
  if (!isPhone) {
    const iconY = 44;
    const iconR = 9;
    const gap = 24;
    const rightEdge = w - pad;
    const positions = [
      { x: rightEdge - iconR * 2 - gap * 2, kind: "search" },
      { x: rightEdge - iconR - gap, kind: "account" },
      { x: rightEdge - iconR, kind: "cart" },
    ];
    for (const p of positions) {
      // Tiny circle for each icon — the cart one has a badge dot to feel
      // like a real shopping cart icon.
      addNode(scene, makeRect({
        x: p.x - iconR / 2, y: iconY - iconR / 2, w: iconR, h: iconR,
        radius: 999,
        fill: { r: 0.16, g: 0.16, b: 0.16, a: 0 },
        strokeWidth: 1.5,
        stroke: { r: 0.16, g: 0.16, b: 0.16, a: 0.7 },
      })); z++;
      if (p.kind === "cart") {
        addNode(scene, makeRect({
          x: p.x + iconR / 2 - 1, y: iconY - iconR / 2 - 1, w: 6, h: 6,
          radius: 999,
          fill: { r: 0.42, g: 0.30, b: 0.92, a: 1 },
        })); z++;
      }
    }
  }

  // Thin divider under header
  addNode(scene, makeRect({
    x: 0, y: 88, w: w, h: 1,
    fill: { r: 0, g: 0, b: 0, a: 0.08 },
  })); z++;

  // === Hero image ===================================================
  // sceneMountT is declared here (BEFORE heroImg.update captures it
  // in its closure) so the rotation animation has a stable t=0.
  const sceneMountT = T();

  // No white card backing. The original Shopify product photos already
  // have their own studio-white background baked in. Adding a backing
  // rect doubled up the white square and made the sway look like a
  // floating window frame instead of the vase itself rotating.
  // The hero image below is the only thing that rotates.

  const heroImg = makeImage({
    x: HERO_X, y: HERO_Y, w: HERO_W, h: HERO_H,
    imageUrl: VASE_HERO_IMAGES[0]!,
    radius: 2,
    tagName: "attic-hero",
    // Supplier master rematted to the exact warm off-white stage color.
    mixBlendMode: "normal",
  });
  addNode(scene, heroImg); z++;
  heroImg.update = (n) => {
    // Cursor parallax — ±6px max
    const px = (cursorNX - 0.5) * 12;
    const py = (cursorNY - 0.5) * 12;
    // Hero nudge on swap — decays over 380ms
    const now = T();
    let nx = 0, ny = 0;
    if (now < heroNudgeUntil) {
      const t = (heroNudgeUntil - now) / 380;
      nx = heroNudgeX * t;
      ny = heroNudgeY * t;
    }
    n.x = HERO_X + px + nx;
    n.y = HERO_Y + py + ny;

    // Hero scale-up on swap — 0.92 → 1.04 (overshoot) → 1.0 over 240ms.
    // Uses transformOrigin so the scale is centered, not corner-anchored.
    n.transformOriginX = 0.5;
    n.transformOriginY = 0.5;
    if (now < heroSwapUntil) {
      const t = (now - heroSwapStartT) / 240;
      // ease-out-back: snappy start, slight overshoot, settle
      const c1 = 1.70158;
      const c3 = c1 + 1;
      const e = 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
      n.scaleX = e;
      n.scaleY = e;
    } else {
      n.scaleX = 1;
      n.scaleY = 1;
    }

    // Continuous slow turntable drift — ±1° at 0.17Hz (period 6s).
    // Supplier images are rematted to the stage color; the tiny angle keeps
    // the motion alive without exposing the corners of the raster canvas.
    n.rotation = Math.sin((now - sceneMountT) / 3000) * (1 * Math.PI / 180);
  };

  // === Helper: rebuild the selected ring around the active thumb =====
  function setActiveRing(idx: number): void {
    if (activeRingId != null) {
      scene.nodes.delete(activeRingId);
      activeRingId = null;
    }
    const ty = THUMBS_Y0 + idx * (THUMB_H + THUMB_GAP);
    const ring = makeRect({
      x: THUMBS_X - 4, y: ty - 4, w: THUMB_W + 8, h: THUMB_H + 8,
      fill: { r: 0, g: 0, b: 0, a: 0 },
      strokeWidth: 2,
      stroke: { r: 0.06, g: 0.06, b: 0.06, a: 1 },
      radius: 2,
      tagName: `attic-thumb-ring-${idx}`,
    });
    scene.nodes.set(ring.id, ring);
    activeRingId = ring.id;
  }

  // === Thumbnails — 4 swap variants ================================
  // Slides in from below (y + 200) with opacity 0, staggered 100ms each.
  // Catches motion-blur on entry AND on every swap.
  const thumbStartBase = sceneMountT + 400;
  for (let i = 0; i < VASE_THUMB_IMAGES.length; i++) {
    const ty = THUMBS_Y0 + i * (THUMB_H + THUMB_GAP);
    const thumb = makeImage({
      x: THUMBS_X, y: ty, w: THUMB_W, h: THUMB_H,
      imageUrl: VASE_THUMB_IMAGES[i]!,
      radius: 2,
      tagName: `attic-thumb-${i}`,
      // Keep supplier photography in normal compositing. Each thumbnail is
      // precomposed at its exact square render ratio.
      mixBlendMode: "normal",
    });
    addNode(scene, thumb); z++;
    const tMount = thumbStartBase + i * 100;
    const tDur = 500;
    thumb.update = (n) => {
      const elapsed = T() - tMount;
      if (elapsed < 0) {
        n.opacity = 0;
        n.y = ty + 200;
      } else if (elapsed < tDur) {
        const t = elapsed / tDur;
        const e = 1 - Math.pow(1 - t, 3); // ease-out cubic
        n.opacity = e;
        n.y = ty + 200 - 200 * e;
      } else {
        // After entry, freeze at final position (don't keep clobbering
        // every frame — saves a write per thumb per frame)
        if (n.y !== ty) n.y = ty;
      }
    };
    thumb.onTap = () => {
      if (activeIdx === i) return;
      activeIdx = i;
      heroImg.imageUrl = VASE_HERO_IMAGES[i]!;
      // Velocity nudge — random direction, 14px magnitude
      const angle = Math.random() * Math.PI * 2;
      heroNudgeX = Math.cos(angle) * 14;
      heroNudgeY = Math.sin(angle) * 14;
      heroNudgeUntil = T() + 380;
      // Hero scale-up — 240ms overshoot ease. The scale velocity feeds
      // the motion-blur postprocess even more visibly than translation
      // does, so the swap reads as motion even at a glance.
      heroSwapStartT = T();
      heroSwapUntil = heroSwapStartT + 240;
      setActiveRing(i);
    };
  }
  // Initial selected ring around thumb 0
  setActiveRing(0);

  // === Product info column ==========================================
  let cy = HERO_Y;

  // Eyebrow — AURUM CRYSTAL. Slides down from -20 → 0 with opacity fade,
  // 600ms, lands first so the title drops in below it.
  const eyebrowStart = sceneMountT + 200;
  const eyebrowDur = 600;
  const eyebrowTargetY = cy;
  const eyebrow = makeText({
    x: COL_X, y: eyebrowTargetY, w: 240, h: 14,
    text: "AURUM CRYSTAL",
    fontSize: 11, fontWeight: 600,
    textColor: { r: 0.16, g: 0.16, b: 0.16, a: 0.7 },
    letterSpacing: 2,
    fontFamily: "system-ui, -apple-system, sans-serif",
  });
  addNode(scene, eyebrow); z++;
  eyebrow.update = (n) => {
    const elapsed = T() - eyebrowStart;
    if (elapsed < 0) {
      n.opacity = 0;
      n.y = eyebrowTargetY - 20;
    } else if (elapsed < eyebrowDur) {
      const t = elapsed / eyebrowDur;
      const e = 1 - Math.pow(1 - t, 3);
      n.opacity = e;
      n.y = eyebrowTargetY - 20 + 20 * e;
    } else {
      if (n.y !== eyebrowTargetY) n.y = eyebrowTargetY;
    }
  };
  cy += 22;

  // Title — large serif wordmark. Slides up from +40 → 0 with opacity
  // 0 → 1 over 800ms. Lands second so it feels like it drops into place.
  const titleStart = sceneMountT + 350;
  const titleDur = 800;
  const titleTargetY = cy;
  const title = makeText({
    x: COL_X, y: titleTargetY, w: COL_W, h: 132,
    text: "Blue Bohemian\nCrystal Flower\nVase",
    fontSize: 40, fontWeight: 400,
    textColor: { r: 0.06, g: 0.06, b: 0.06, a: 1 },
    letterSpacing: -0.5,
    lineHeight: 1.05,
    fontFamily: "Georgia, 'Times New Roman', serif",
  });
  addNode(scene, title); z++;
  title.update = (n) => {
    const elapsed = T() - titleStart;
    if (elapsed < 0) {
      n.opacity = 0;
      n.y = titleTargetY + 40;
    } else if (elapsed < titleDur) {
      const t = elapsed / titleDur;
      const e = 1 - Math.pow(1 - t, 3);
      n.opacity = e;
      n.y = titleTargetY + 40 - 40 * e;
    } else {
      if (n.y !== titleTargetY) n.y = titleTargetY;
    }
  };
  cy += 138;

  // Price — animated entry (scale 1.4 → 1.0 with overshoot, 500ms)
  // then continuous breathing. Two-phase update so it reads as motion
  // on entry AND on every still frame.
  const priceNode = makeText({
    x: COL_X, y: cy, w: COL_W, h: 22,
    text: "$140.00 USD",
    fontSize: 18, fontWeight: 500,
    textColor: { r: 0.06, g: 0.06, b: 0.06, a: 0.85 },
    letterSpacing: 0.2,
    fontFamily: "system-ui, -apple-system, sans-serif",
  });
  addNode(scene, priceNode); z++;
  const priceEntryStart = sceneMountT + 800; // delay so it lands after title
  const priceEntryDur = 500;
  priceNode.update = (n) => {
    const elapsed = T() - priceEntryStart;
    // Entry overshoot
    if (elapsed >= 0 && elapsed < priceEntryDur) {
      const t = elapsed / priceEntryDur;
      const c1 = 1.70158;
      const c3 = c1 + 1;
      const e = 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
      n.scaleX = e * 1.0;
      n.scaleY = e * 1.0;
      n.opacity = t;
    } else {
      n.scaleX = 1;
      n.scaleY = 1;
      // Continuous breathing — visible alpha pulse, 0.55 → 1.0
      n.opacity = 0.78 + Math.abs(Math.sin(T() / 1100)) * 0.22;
    }
  };
  priceNode.transformOriginX = 0;
  priceNode.transformOriginY = 0.5;
  cy += 36;

  // "Taxes included. FREE SHIPPING."
  addNode(scene, makeText({
    x: COL_X, y: cy, w: COL_W, h: 14,
    text: "Taxes included.  FREE SHIPPING.",
    fontSize: 11, fontWeight: 400,
    textColor: { r: 0.16, g: 0.16, b: 0.16, a: 0.7 },
    fontFamily: "system-ui, -apple-system, sans-serif",
  })); z++;
  cy += 28;

  // === CTA — Add to cart (primary), Buy with Shop (secondary) =======
  // Both CTAs fade in from gray → full color over 700ms, staggered 120ms.
  // Add to cart fades to BLACK, Buy with shop fades to PURPLE.
  const ctaY = cy;
  const ctaAddBg = makeRect({
    x: COL_X, y: ctaY, w: COL_W, h: 48,
    fill: { r: 0.06, g: 0.06, b: 0.06, a: 1 },
    radius: 2,
    tagName: "attic-cta-add",
  });
  addNode(scene, ctaAddBg); z++;
  const ctaAddText = makeText({
    x: COL_X, y: ctaY + 16, w: COL_W, h: 18,
    text: "Add to cart",
    fontSize: 14, fontWeight: 500,
    textColor: { r: 1, g: 1, b: 1, a: 1 },
    textAlign: "center",
    letterSpacing: 0.4,
    fontFamily: "system-ui, -apple-system, sans-serif",
  });
  addNode(scene, ctaAddText); z++;
  const ctaAddStart = sceneMountT + 1100;
  const ctaAddDur = 700;
  const ctaAddUpdate = (n: typeof ctaAddBg) => {
    const elapsed = T() - ctaAddStart;
    if (elapsed < 0) {
      n.opacity = 0;
    } else if (elapsed < ctaAddDur) {
      const t = elapsed / ctaAddDur;
      const e = 1 - Math.pow(1 - t, 3); // ease-out cubic
      n.opacity = e;
    } else {
      n.opacity = 1;
    }
  };
  ctaAddBg.update = ctaAddUpdate;
  ctaAddText.update = ctaAddUpdate;
  cy += 60;

  // Buy with Shop — purple pill, matches the real PDP exactly
  const ctaBuyBg = makeRect({
    x: COL_X, y: cy, w: COL_W, h: 48,
    fill: { r: 0.42, g: 0.30, b: 0.92, a: 1 },
    radius: 2,
    tagName: "attic-cta-buy",
  });
  addNode(scene, ctaBuyBg); z++;
  const ctaBuyText = makeText({
    x: COL_X, y: cy + 16, w: COL_W, h: 18,
    text: "Buy with shop",
    fontSize: 14, fontWeight: 500,
    textColor: { r: 1, g: 1, b: 1, a: 1 },
    textAlign: "center",
    letterSpacing: 0.4,
    fontFamily: "system-ui, -apple-system, sans-serif",
  });
  addNode(scene, ctaBuyText); z++;
  const ctaBuyStart = sceneMountT + 1220; // 120ms after Add-to-cart
  const ctaBuyDur = 700;
  const ctaBuyUpdate = (n: typeof ctaBuyBg) => {
    const elapsed = T() - ctaBuyStart;
    if (elapsed < 0) {
      n.opacity = 0;
    } else if (elapsed < ctaBuyDur) {
      const t = elapsed / ctaBuyDur;
      const e = 1 - Math.pow(1 - t, 3);
      n.opacity = e;
    } else {
      n.opacity = 1;
    }
  };
  ctaBuyBg.update = ctaBuyUpdate;
  ctaBuyText.update = ctaBuyUpdate;
  cy += 68;

  // === Description — the real copy, word-for-word ===================
  const DESC = "Crafted in the Czech Republic, this 12.5-inch Aurum Crystal vase is made from high-quality, durable, lead-free Bohemian crystal. It beautifully blends traditional craftsmanship with advanced technology, resulting in a chic blue bud vase designed to bring elegance and sophistication to any home or business decor.";
  addNode(scene, makeText({
    x: COL_X, y: cy, w: COL_W, h: 180,
    text: DESC,
    fontSize: 13, fontWeight: 400,
    textColor: { r: 0.16, g: 0.16, b: 0.16, a: 0.78 },
    lineHeight: 1.5,
    fontFamily: "system-ui, -apple-system, sans-serif",
  })); z++;
  cy += 184;

  // === Spec rows — slide-in cascade =================================
  const SPECS: [string, string][] = [
    ["Length", "7\""],
    ["Width", "7\""],
    ["Height", "12.5\""],
    ["Weight", "8.16 lbs"],
    ["Made in", "Czechia"],
  ];
  const specStartBase = sceneMountT + 1500; // start specs AFTER both CTAs land
  for (let i = 0; i < SPECS.length; i++) {
    const [label, value] = SPECS[i]!;
    const rowY = cy + i * 28;
    const spec = makeText({
      x: COL_X, y: rowY, w: COL_W, h: 16,
      text: `${label}:  ${value}`,
      fontSize: 12, fontWeight: 500,
      textColor: { r: 0.06, g: 0.06, b: 0.06, a: 0.85 },
      fontFamily: "system-ui, -apple-system, sans-serif",
      tagName: `attic-spec-${i}`,
    });
    addNode(scene, spec); z++;
    const mountStart = specStartBase + i * 100;
    const DURATION = 500;
    spec.update = (n) => {
      const elapsed = T() - mountStart;
      if (elapsed < 0) {
        n.opacity = 0;
        n.x = COL_X - 120;
      } else if (elapsed < DURATION) {
        const t = elapsed / DURATION;
        const e = 1 - Math.pow(1 - t, 3); // ease-out cubic
        n.opacity = e;
        n.x = COL_X - 120 + 120 * e;
      }
    };
  }

  // (No onTick needed — the hero drift, rotation, and price breathing
  //  are all driven by T() inside their respective node update
  //  callbacks, which the engine ticks every frame for us.)

  // === Footer hint — "built with NSPP" ==============================
  addNode(scene, makeText({
    x: pad, y: VH - 36, w: 360, h: 14,
    text: "Built with NSPP — same content as the Shopify PDP, rendered as motion.",
    fontSize: 10, fontWeight: 500,
    textColor: { r: 0.16, g: 0.16, b: 0.16, a: 0.5 },
    letterSpacing: 0.4,
    fontFamily: "system-ui, -apple-system, sans-serif",
  })); z++;

  // (No onTick needed — the hero drift and price breathing are driven by
  //  T() inside their respective node update callbacks, which the
  //  engine ticks every frame for us.)

  return scene;
}
